import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { Home, Server, Network, UserPlus, LogOut, Shield, Settings, Radio } from 'lucide-react'
import { authApi } from '../../api/client'
import { useAuth } from '../../context/AuthContext'
import clsx from 'clsx'

interface NavItem {
  to: string
  icon: React.ComponentType<{ className?: string }>
  label: string
  adminOnly?: boolean
}

const navItems: NavItem[] = [
  { to: '/', icon: Home, label: 'Dashboard' },
  { to: '/devices', icon: Server, label: 'Devices' },
  { to: '/tunnels', icon: Network, label: 'Tunnels' },
  { to: '/enroll', icon: UserPlus, label: 'Enroll' },
  { to: '/relays', icon: Radio, label: 'Relays', adminOnly: true },
  { to: '/settings', icon: Settings, label: 'Settings' },
]

export default function Layout() {
  const navigate = useNavigate()
  const { operator } = useAuth()

  const handleLogout = async () => {
    await authApi.logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-slate-800 border-r border-slate-700">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-700">
            <Shield className="w-8 h-8 text-primary-500" />
            <span className="text-xl font-bold text-white">Gatewarden</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1">
            {navItems
              .filter(item => !item.adminOnly || operator?.role === 'admin')
              .map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  )
                }
              >
                <Icon className="w-5 h-5" />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="px-4 py-4 border-t border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white">
                  {operator?.name || operator?.email}
                </p>
                <p className="text-xs text-slate-400">{operator?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="pl-64">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
